import { X, Plus, Minus, Trash2, GripVertical, ChevronDown } from "lucide-react";
import { RiyalSymbol } from "./RiyalIcon";

/**
 * Option group builder inside product dialog.
 * value: array of groups [{ id, name, required, min, max, options: [{id, name, price_delta}] }]
 * onChange: (groups) => void
 */
export default function OptionGroupsEditor({
  value = [],
  onChange,
  errors = {},
  onFieldChange,
}) {
  const groups = value || [];

  const updateGroup = (idx, patch) => {
    const copy = [...groups]; copy[idx] = { ...copy[idx], ...patch }; onChange(copy);
  };
  const removeGroup = (idx) => {
    onChange(groups.filter((_, i) => i !== idx));
    onFieldChange?.("option-groups");
  };
  const addGroup = () => {
    onChange([...groups, {
      id: crypto.randomUUID(), name: "", required: false, min: 0, max: null, options: [],
    }]);
  };
  const toggleMaximum = (index) => {
    const group = groups[index];
    const maximum = group.max === null
      ? Math.max(1, Number(group.min) || 1)
      : null;
    updateGroup(index, { max: maximum });
    onFieldChange?.(`option-groups.${index}.max`);
  };
  const addOption = (gi) => {
    const g = groups[gi];
    updateGroup(gi, {
      options: [...(g.options || []), { id: crypto.randomUUID(), name: "", price_delta: 0 }],
    });
    onFieldChange?.(`option-groups.${gi}`);
  };
  const removeOption = (gi, oi) => {
    const g = groups[gi];
    updateGroup(gi, { options: g.options.filter((_, i) => i !== oi) });
    onFieldChange?.(`option-groups.${gi}`);
  };
  const updateOption = (gi, oi, patch) => {
    const g = groups[gi];
    const opts = [...g.options]; opts[oi] = { ...opts[oi], ...patch };
    updateGroup(gi, { options: opts });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-extrabold">مجموعات الخيارات</div>
        <button type="button" onClick={addGroup} className="pill bg-white border border-orange-200 text-orange-700 text-xs font-bold px-3 py-1.5 inline-flex items-center gap-1" data-testid="add-option-group">
          <Plus size={14}/> إضافة مجموعة
        </button>
      </div>

      {groups.length === 0 && (
        <div className="text-xs text-gray-500 bg-orange-50 border border-dashed border-orange-200 rounded-xl p-3 text-center">
          لا توجد مجموعات — يمكنك إضافة "اختر الحجم" أو "إضافات" مثلاً
        </div>
      )}

      {groups.map((g, gi) => (
        <div key={g.id || gi} className="rounded-2xl border border-orange-100 bg-orange-50/40 p-3" data-testid={`option-group-${gi}`}>
          <div className="flex items-center gap-2">
            <GripVertical className="text-gray-300" size={16}/>
            <input
              value={g.name}
              onChange={e => {
                updateGroup(gi, { name: e.target.value });
                onFieldChange?.(`option-groups.${gi}.name`);
              }}
              placeholder="اسم المجموعة (مثال: اختر الحجم)"
              className={
                "flex-1 h-10 rounded-xl bg-white border px-3 text-sm outline-none "
                + (errors[`option-groups.${gi}.name`]
                  ? "border-red-500 focus:border-red-500"
                  : "border-gray-200 focus:border-orange-500")
              }
              data-testid={`og-name-${gi}`}
            />
            <button type="button" onClick={() => removeGroup(gi)} className="w-9 h-9 rounded-xl bg-white text-red-500 border border-red-100" data-testid={`og-del-${gi}`}><Trash2 size={16} className="mx-auto"/></button>
          </div>
          {errors[`option-groups.${gi}.name`] && (
            <p
              className="mt-1 text-xs text-red-600"
              data-testid={`og-name-error-${gi}`}
            >
              {errors[`option-groups.${gi}.name`]}
            </p>
          )}

          <div className="flex items-center gap-3 mt-2 text-xs">
            <span className="text-gray-500">الحد الأدنى</span>
            <div>
              <input
                type="number"
                min="0"
                value={g.min}
                onChange={e => {
                  updateGroup(gi, { min: Number(e.target.value) });
                  onFieldChange?.(`option-groups.${gi}.min`);
                  onFieldChange?.(`option-groups.${gi}.max`);
                }}
                className={`w-14 h-8 rounded-lg bg-white border px-2 num-badge ${
                  errors[`option-groups.${gi}.min`] ? "border-red-500" : "border-gray-200"
                }`}
                data-testid={`og-min-${gi}`}
              />
              {errors[`option-groups.${gi}.min`] && (
                <p
                  className="mt-1 max-w-32 text-xs text-red-600"
                  data-testid={`og-min-error-${gi}`}
                >
                  {errors[`option-groups.${gi}.min`]}
                </p>
              )}
            </div>
            {g.max === null ? (
              <button
                type="button"
                onClick={() => toggleMaximum(gi)}
                className="inline-flex items-center gap-1 text-gray-500"
                data-testid={`og-max-toggle-${gi}`}
              >
                <span>الحد الأقصى</span>
                <span>بلا حد أقصى</span>
                <ChevronDown size={14} />
              </button>
            ) : (
              <span className="inline-flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => toggleMaximum(gi)}
                  className="inline-flex items-center gap-1 text-gray-500"
                  data-testid={`og-max-toggle-${gi}`}
                >
                  <span>الحد الأقصى</span>
                  <span>{`حتى ${g.max}`}</span>
                  <ChevronDown size={14} className="rotate-180" />
                </button>
                <div>
                  <input
                    type="number"
                    min="1"
                    value={g.max}
                    onChange={e => {
                      updateGroup(gi, { max: Number(e.target.value) });
                      onFieldChange?.(`option-groups.${gi}.max`);
                      onFieldChange?.(`option-groups.${gi}.min`);
                    }}
                    className={`w-14 h-8 rounded-lg bg-white border px-2 num-badge ${
                      errors[`option-groups.${gi}.max`] ? "border-red-500" : "border-gray-200"
                    }`}
                    data-testid={`og-max-${gi}`}
                  />
                  {errors[`option-groups.${gi}.max`] && (
                    <p
                      className="mt-1 max-w-32 text-xs text-red-600"
                      data-testid={`og-max-error-${gi}`}
                    >
                      {errors[`option-groups.${gi}.max`]}
                    </p>
                  )}
                </div>
              </span>
            )}
          </div>

          <div className="mt-2 space-y-2">
            {(g.options || []).map((o, oi) => (
              <div
                key={o.id || oi}
                className="space-y-1"
                data-testid={`option-${gi}-${oi}`}
              >
                <div className="flex items-center gap-2">
                  <input
                    value={o.name}
                    onChange={e => {
                      updateOption(gi, oi, { name: e.target.value });
                      onFieldChange?.(`option-groups.${gi}.options.${oi}.name`);
                    }}
                    placeholder="اسم الخيار"
                    className={
                      "flex-1 h-10 rounded-xl bg-white border px-3 text-sm outline-none "
                      + (errors[`option-groups.${gi}.options.${oi}.name`]
                        ? "border-red-500 focus:border-red-500"
                        : "border-gray-200 focus:border-orange-500")
                    }
                    data-testid={`opt-name-${gi}-${oi}`}
                  />
                  <div className="flex flex-col">
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        value={o.price_delta}
                        onChange={e => {
                          updateOption(gi, oi, { price_delta: Number(e.target.value) });
                          onFieldChange?.(`option-groups.${gi}.options.${oi}.price_delta`);
                        }}
                        placeholder="+0"
                        className={
                          "w-20 h-10 rounded-xl bg-white border px-2 text-sm text-center "
                          + "num-badge focus:outline-none "
                          + (errors[`option-groups.${gi}.options.${oi}.price_delta`]
                            ? "border-red-500"
                            : "border-gray-200 focus:border-orange-500")
                        }
                        data-testid={`opt-price-${gi}-${oi}`}
                      />
                      <RiyalSymbol className="h-3" />
                    </div>
                    {errors[`option-groups.${gi}.options.${oi}.price_delta`] && (
                      <p
                        className="mt-1 max-w-24 text-xs text-red-600"
                        data-testid={`opt-price-error-${gi}-${oi}`}
                      >
                        {errors[`option-groups.${gi}.options.${oi}.price_delta`]}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeOption(gi, oi)}
                    className="h-9 w-9 rounded-xl border border-red-100 bg-white text-red-500"
                    data-testid={`opt-delete-${gi}-${oi}`}
                    aria-label="إزالة الخيار"
                  >
                    <X size={14} className="mx-auto"/>
                  </button>
                </div>
                {errors[`option-groups.${gi}.options.${oi}.name`] && (
                  <p
                    className="text-xs text-red-600"
                    data-testid={`opt-name-error-${gi}-${oi}`}
                  >
                    {errors[`option-groups.${gi}.options.${oi}.name`]}
                  </p>
                )}
              </div>
            ))}
            <button type="button" onClick={() => addOption(gi)} className="w-full pill bg-white border border-dashed border-orange-300 text-orange-700 text-xs font-bold py-2 inline-flex items-center justify-center gap-1" data-testid={`og-add-opt-${gi}`}>
              <Plus size={14}/> إضافة خيار
            </button>
            {errors[`option-groups.${gi}.options`] && (
              <p className="text-xs text-red-600" data-testid={`og-options-error-${gi}`}>
                {errors[`option-groups.${gi}.options`]}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
